import React, { useState, useEffect, useMemo } from "react";
import "./AnalysisDashboard.css";
import { processApi } from "../../../API/api";
import { isRunRegenerable } from "./regenerateDeliverablesPure";
import {
  transformProgressData,
  shortenRunDisplayId,
  SERVICE_DISPLAY_NAMES,
  SERVICE_ORDER,
} from "../../../utils/progressDataTransformer";
import {
  PHASES,
  normalizePhase,
  serverPhase,
  serverOverallPercent,
} from "../RSAAnalysis/InternalComponents/runLabel";
import { RunTitle, RunIdTail } from "../RunQueue/RunLabelParts";
import OverviewLinks from "../../Overview/OverviewLinks";

const RSA_MODEL_PROJECT_TYPES = new Set([
  "rsa-model",
  "rsa-existing",
  "rsa_existing",
]);

// Phase C-1: deep links into AWS console for ops debugging.
// run_id IS the SF execution name (engine=v2 dispatch sets it that way), so
// the execution ARN is deterministic and the SF console URL is shareable.
const AWS_REGION = "us-east-1";
const AWS_ACCOUNT = "401251961508";
const SF_STATE_MACHINE_NAME = "rsa-chain";

const buildSfConsoleUrl = (runId) => {
  if (!runId) return null;
  const arn = `arn:aws:states:${AWS_REGION}:${AWS_ACCOUNT}:execution:${SF_STATE_MACHINE_NAME}:${runId}`;
  // SF console uses URL-encoded ARN as the state path.
  return `https://${AWS_REGION}.console.aws.amazon.com/states/home?region=${AWS_REGION}#/v2/executions/details/${encodeURIComponent(arn)}`;
};

/** Google Drive analysis template IDs → labels (aligned with RSAAnalysis RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING). */
const RSA_MODEL_TEMPLATE_ID_TO_LABEL = {
  "1utdS8VLw2hoCf00xZbXhYtcTsWnWDdsm": "Intersection Sight Distance (ISD)",
  "10bkKVtyerK5Qe20g7Rc15ncwDQn4daih": "Passing Sight Distance (PSD)",
  "1rTfTDLA48ET--fAduVGsI0ad3LFssmLC": "Stopping Sight Distance (SSD)",
  "1UATrkaa_ASvlJejyfi0TekiM3nBfO9lC": "Flat Spot Analysis (FSA)",
  "1irrUhf9npguUDAxHy29tILDiv7DTVO0t": "Sunglare Analysis",
  "1ElRBuXgNAHakGVVw11671QAn34IeQQKN": "Horizontal Alignment",
};

const RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL = {
  SSD: "Stopping Sight Distance (SSD)",
  PSD: "Passing Sight Distance (PSD)",
  FSA: "Flat Spot Analysis (FSA)",
  ISD: "Intersection Sight Distance (ISD)",
  GLR: "Sunglare Analysis",
  Horizontal: "Horizontal Alignment",
  HORIZONTAL: "Horizontal Alignment",
};

/**
 * Human-readable analyses from the rsa-model launch payload (or optimistic selected task names).
 * Backend stores `metadata.launch_payload` with `analysis_template_ids`; `metadata.analysis_types` is a fallback.
 */
const getRsaModelPayloadAnalysisLabels = (run) => {
  if (!run) return [];
  const launch = run.metadata?.launch_payload;
  const ids = launch?.analysis_template_ids;
  if (Array.isArray(ids) && ids.length > 0) {
    return ids.map((id) => {
      const key = String(id ?? "").trim();
      return RSA_MODEL_TEMPLATE_ID_TO_LABEL[key] || key;
    });
  }
  const analysisTypes = run.metadata?.analysis_types;
  if (Array.isArray(analysisTypes) && analysisTypes.length > 0) {
    return analysisTypes
      .map((t) => {
        const s = String(t ?? "").trim();
        if (!s) return "";
        return (
          RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL[s] ||
          RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL[s.toUpperCase()] ||
          s
        );
      })
      .filter(Boolean);
  }
  const svc = run.services;
  if (!Array.isArray(svc) || svc.length === 0) return [];
  const looksLikePipelineKey = (str) => {
    const s = String(str || "").trim();
    if (!s) return false;
    if (s.includes("(") || /sight distance|analysis/i.test(s)) return false;
    return /^[a-z][a-z0-9_]*$/i.test(s) && s.includes("_");
  };
  if (svc.some(looksLikePipelineKey)) return [];
  return svc.map((s) => String(s).trim()).filter(Boolean);
};

// engine=v2 emits its chunk-parallel preprocessing stages (tiling, csf,
// road_detection, car_removal, file_transfer) as separate services. They
// all conceptually belong to the "Pre-Process" bucket in the user's mental
// model (renamed from "File Transfer" 2026-07-29 — the PHASE covers all
// preprocessing; only its file_transfer SUB-STAGE actually transfers
// files and keeps that name). engine=v1 emits only `file_transfer` for
// the same logical stage. The internal `name` stays "file_transfer" — it
// keys activePhase state and the progress-band mapping.
const DELIVERABLE_SERVICE_NAMES = [
  "rsa_report",
  "rsa_video",
  "shapefile",
  "automatic_reports",
  "striping_plan",
  "automatic_report",
  "report_generation",
];

const RSA_MODEL_STEP_GROUPS = [
  {
    name: "file_transfer",
    display_name: "Pre-Process",
    description: "Preparing the point cloud: tiling, ground classification, road detection, vehicle removal, file transfer",
    defaultProgress: 20,
    services: [
      "tiling",
      "csf",
      "road_detection",
      "car_removal",
      "file_transfer",
    ],
  },
  {
    name: "rsa_analysis",
    display_name: "RSA Analysis",
    description: "Running RSA and analysis services",
    defaultProgress: 55,
    // "rsa_analysis" is the pyexisting python-analysis fan-out's own progress
    // key (the chunked SSD/PSD/ISD/FSA/GLR windows — the bulk of phase 2).
    // It was missing here, so pyexisting runs showed a phase-2 stage list
    // with only create_rsa + the classifiers and none of the actual analysis
    // work (reported 2026-07-29: "not showing the RSA stages").
    services: [
      "create_rsa",
      "rsa_analysis",
      // The python-analysis WORKERS emit under their own service names
      // (observed live 2026-07-29: rsa_fsa etc. with per-chunk messages) —
      // these are the real per-type rows. The combined "rsa_analysis" row
      // is hidden whenever any of them has data (see subStages below).
      "rsa_ssd",
      "rsa_psd",
      "rsa_isd",
      "rsa_fsa",
      "rsa_glr",
      "ssd_classification",
      "psd_classification",
      "horizontal",
      "embankment",
      "clear_zone",
      "traffic_signs",
      "road_markings",
      "road_markings_lidar",
      "guardrail",
      // guardrail_warrant is the key the pyexisting chain seeds + emits for
      // GRW; `guardrail` is the legacy key. GRW was invisible without it
      // (reported 2026-09-08).
      "guardrail_warrant",
    ],
  },
  {
    name: "final_reports",
    display_name: "Reports & Deliverables",
    // Shown instead of display_name on the step-title button ≤768px, where the
    // full label wraps to two lines in a chip that only has room for one.
    mobile_display_name: "Deliverables",
    description: "Generating final report outputs",
    defaultProgress: 85,
    // Every deliverable stage of a run (2026-09-08): the online report
    // frames, the drive-through videos, the shapefile/CSV/DXF bundle and the
    // PDF reports. Regeneration runs seed only the selected subset — any
    // subset of these rows must work.
    services: DELIVERABLE_SERVICE_NAMES,
  },
];

const normalizeServiceStatus = (serviceData) => {
  const progress = Number(serviceData?.progress || 0);
  const raw = String(serviceData?.status || "")
    .trim()
    .toLowerCase();
  if (raw === "failed") return "failed";
  if (raw === "completed" || progress >= 100) return "completed";
  if (raw === "processing" || raw === "running" || progress > 0)
    return "running";
  return "pending";
};

const clampProgress = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

// Overall progress is a DISPLAY value: it never regresses once shown (2026-09-06).
// The band model below drives each phase by the GROUP AVERAGE of every service
// present, so the raw number falls every time a newly-reporting service joins
// at 0% or a phase flips — which read as the bar "going back" many times per
// run. Per run id: shown = max(shown, raw); while raw < shown and the run is
// still running, creep +1 at most once per PROGRESS_CREEP_MS (≈ one poll) up
// to 99, so the bar keeps moving slowly until reality catches up. completed
// → 100; failed → raw (an honest failed bar); an unseen run starts at raw.
const _shownProgressByRun = new Map(); // runId -> { shown, at }
const PROGRESS_CREEP_MS = 45000;
const monotonicPercent = (runId, rawPct, status) => {
  const raw = clampProgress(rawPct);
  const key = runId ? String(runId) : "";
  const st = String(status || "").toLowerCase();
  if (!key) return raw;
  if (st === "completed") {
    _shownProgressByRun.set(key, { shown: 100, at: Date.now() });
    return 100;
  }
  if (st === "failed") return raw;
  const now = Date.now();
  const prev = _shownProgressByRun.get(key);
  if (!prev) {
    _shownProgressByRun.set(key, { shown: raw, at: now });
    return raw;
  }
  let { shown, at } = prev;
  if (raw >= shown) {
    shown = raw;
    at = now;
  } else if (st === "running" && shown < 99 && now - at >= PROGRESS_CREEP_MS) {
    shown += 1;
    at = now;
  }
  _shownProgressByRun.set(key, { shown, at });
  return shown;
};

const getServiceData = (services, serviceName) => {
  const data = services?.[serviceName];
  return data && typeof data === "object" ? data : null;
};

const getFirstServiceData = (services, serviceNames) => {
  for (const name of serviceNames) {
    const data = getServiceData(services, name);
    if (data) return data;
  }
  return null;
};

const getServiceMessage = (serviceData) =>
  String(serviceData?.message || "")
    .trim()
    .toLowerCase();

const GENERIC_PROGRESS_MESSAGES = new Set([
  "",
  "initializing...",
  "waiting...",
  "waiting",
  "in progress",
  "completed",
]);

const isMeaningfulProgressMessage = (message) => {
  const normalized = String(message || "")
    .trim()
    .toLowerCase();
  return normalized.length > 0 && !GENERIC_PROGRESS_MESSAGES.has(normalized);
};

const isServiceStarted = (serviceData) => {
  if (!serviceData || typeof serviceData !== "object") return false;
  const status = normalizeServiceStatus(serviceData);
  if (status === "running" || status === "completed" || status === "failed")
    return true;
  return isMeaningfulProgressMessage(getServiceMessage(serviceData));
};

// Run-queue pre-execution states (perf wave 2). A run waiting for admission
// has NOT started the pipeline: it maps to 0% and a "waiting for capacity"
// message, never to the Pre-process band (which would imply work in flight).
const QUEUE_PRE_EXECUTION_STATUSES = new Set(["queued", "cancelled"]);
const QUEUE_STATE_MESSAGES = {
  queued: "Queued — waiting for GPU capacity",
  held: "Queued — not started (an admin starts it from Multi-project runs)",
  cancelled: "Cancelled before start",
};

export const queuePreExecutionStatus = (run, progress) => {
  const services =
    (progress && typeof progress.services === "object" && progress.services) ||
    {};
  const runKeyStatus = String(services?.run?.status || "")
    .trim()
    .toLowerCase();
  if (QUEUE_PRE_EXECUTION_STATUSES.has(runKeyStatus)) return runKeyStatus;
  const runStatus = String(run?.status || "")
    .trim()
    .toLowerCase();
  // The task list already derives "queued"/"cancelled" from the run key; a
  // stale progress object must not override a fresher list status.
  if (QUEUE_PRE_EXECUTION_STATUSES.has(runStatus) && !runKeyStatus)
    return runStatus;
  if (QUEUE_PRE_EXECUTION_STATUSES.has(runStatus) && runKeyStatus === "pending")
    return runStatus;
  return null;
};

// A HELD run (2026-09-08 evening: queued by an admin without starting it) is
// "queued" to the task list and the progress tracker; the enqueue seeds
// queue_status "held" in the run metadata and a "not started" run message.
const isHeldQueuedRun = (run, progress) => {
  const meta = String(run?.metadata?.queue_status || run?.queue?.status || "")
    .trim()
    .toLowerCase();
  if (meta === "held") return true;
  const message = String(progress?.services?.run?.message || "").toLowerCase();
  return message.includes("not started");
};

const queueStateMessage = (queueStatus, run, progress) =>
  queueStatus === "queued" && isHeldQueuedRun(run, progress)
    ? QUEUE_STATE_MESSAGES.held
    : QUEUE_STATE_MESSAGES[queueStatus];

const buildQueuedOverview = (queueStatus, run = null, progress = null) => {
  const message = queueStateMessage(queueStatus, run, progress) || "Waiting";
  const steps = RSA_MODEL_STEP_GROUPS.map((group) => ({
    name: group.name,
    display_name: group.display_name,
    mobile_display_name: group.mobile_display_name || null,
    status: "pending",
    progress: 0,
    message,
    started_at: null,
    completed_at: null,
    duration: null,
    description: group.description,
    instance_id: null,
    folder_id: null,
    folder_name: null,
    sub_stages: [],
  }));
  return {
    status: queueStatus,
    progress: { percentage: 0, completed_steps: 0, total_steps: steps.length },
    steps,
    queue_message: message,
  };
};

export const buildRsaModelOverview = (run, progress) => {
  const queueStatus = queuePreExecutionStatus(run, progress);
  if (queueStatus) return buildQueuedOverview(queueStatus, run, progress);
  const services =
    (progress && typeof progress.services === "object" && progress.services) ||
    {};
  // The 'run' progress key carries the SF-execution terminal status (posted
  // by the progress translator). It is authoritative over both the task-list
  // status and per-service bookkeeping: pre-seeded stages that never report
  // would otherwise keep a finished run pinned at "running 99%".
  const runKeyStatus = String(
    getServiceData(services, "run")?.status || "",
  )
    .trim()
    .toLowerCase();
  const terminalRunKeyStatus =
    runKeyStatus === "completed" || runKeyStatus === "failed"
      ? runKeyStatus
      : "";
  const runStatus =
    terminalRunKeyStatus ||
    String(run?.status || "")
      .trim()
      .toLowerCase();
  const createRsaData = getServiceData(services, "create_rsa");
  const fileTransferData = getServiceData(services, "file_transfer");
  const finalReportData = getFirstServiceData(services, [
    "automatic_report",
    "automatic_reports",
    "report_generation",
  ]);
  const analysisServiceNames = [
    "rsa_analysis",
    "rsa_ssd",
    "rsa_psd",
    "rsa_isd",
    "rsa_fsa",
    "rsa_glr",
    "ssd_classification",
    "psd_classification",
    "horizontal",
    "embankment",
    "clear_zone",
    "traffic_signs",
    "road_markings",
    "road_markings_lidar",
    "guardrail",
    "guardrail_warrant",
  ];

  const createRsaStarted = isServiceStarted(createRsaData);
  const createRsaCompleted =
    normalizeServiceStatus(createRsaData) === "completed";

  // Phase start/complete signals (used for phase selection + band floors).
  // Per-service progress NUMBERS are no longer needed here — phase % now
  // comes from each group's average (groupStats[i].progress) below.
  const analysisStarted = analysisServiceNames.some((name) =>
    isServiceStarted(getServiceData(services, name)),
  );

  // Deliverables aggregate over EVERY present deliverable row (2026-09-08):
  // started when any started, completed only when all present rows are
  // completed. rsa_report finishes early on pyexisting runs ("publish-only:
  // frames published, render deferred") and rsa_video runs inside the
  // analysis branches, so neither may on its own flip the run into phase 3 —
  // that stays keyed to the PDF-report row (below) or the post-analysis
  // shapefile stage.
  const deliverableRows = DELIVERABLE_SERVICE_NAMES.map((name) =>
    getServiceData(services, name),
  ).filter((d) => d && typeof d === "object");
  const finalReportsStarted = deliverableRows.some((d) => isServiceStarted(d));
  const finalReportsCompleted =
    deliverableRows.length > 0 &&
    deliverableRows.every((d) => normalizeServiceStatus(d) === "completed");
  const pdfReportProgress = clampProgress(finalReportData?.progress);
  const pdfReportMessage = getServiceMessage(finalReportData);
  const pdfReportStarted = isServiceStarted(finalReportData);
  const automaticReportInstanceLaunched =
    pdfReportStarted &&
    (pdfReportMessage.includes("waiting for instance") ||
      pdfReportMessage.includes("sending request to instance") ||
      pdfReportProgress >= 10);
  const shapefileStarted = isServiceStarted(getServiceData(services, "shapefile"));

  const groupStats = RSA_MODEL_STEP_GROUPS.map((group) => {
    const present = group.services
      .map((name) => ({ name, data: services[name] }))
      .filter((item) => item.data && typeof item.data === "object");

    const statuses = present.map((item) => normalizeServiceStatus(item.data));
    // Average over STARTED services once any service in the group is active.
    // Pre-seeded pending stages used to dilute the average to near-zero for
    // hours (reported 2026-07-29: RSA Analysis showed 5% while the fan-out
    // was at 278/396 chunks — create_rsa 100 + rsa_analysis 70 averaged with
    // four untouched classifiers reads as "hung"). Until anything starts,
    // fall back to the all-present average (0) as before.
    const started = present.filter((item) => isServiceStarted(item.data));
    const progressSource = started.length > 0 ? started : present;
    const progresses = progressSource
      .map((item) => Number(item.data.progress))
      .filter((n) => Number.isFinite(n));
    const avgProgress =
      progresses.length > 0
        ? Math.round(progresses.reduce((a, b) => a + b, 0) / progresses.length)
        : 0;

    // engine=v2 pre-seeds every stage as 'pending' at run dispatch
    // (via progress_tracker.initialize_run), so a phase with "some done,
    // some pending" used to slip past both `running` and `completed`
    // and the dashboard would render 0%. Treat a phase that has any
    // active/done service (but isn't fully done) as `running`.
    const allCompleted =
      statuses.length > 0 && statuses.every((s) => s === "completed");
    const hasAnyActive = statuses.some(
      (s) => s === "running" || s === "completed" || s === "failed",
    );
    return {
      ...group,
      seen: present.length > 0,
      failed: statuses.includes("failed"),
      running: statuses.includes("running") || (hasAnyActive && !allCompleted),
      completed: allCompleted,
      progress: avgProgress,
    };
  });

  const hasSeenGroups = groupStats.some((group) => group.seen);
  const hasStartedWork =
    isServiceStarted(fileTransferData) ||
    createRsaStarted ||
    analysisStarted ||
    finalReportsStarted ||
    groupStats.some(
      (group) => group.failed || group.running || group.completed,
    );

  // Treat run as effectively completed if all seen step groups are done,
  // even if the run-level status field hasn't been updated yet (e.g. after video timeout + continue).
  const allGroupsCompleted =
    hasSeenGroups && groupStats.every((g) => !g.seen || g.completed);
  const isEffectivelyCompleted =
    runStatus === "completed" || allGroupsCompleted;

  let phase = 1;
  if (
    isEffectivelyCompleted ||
    finalReportsCompleted ||
    automaticReportInstanceLaunched ||
    shapefileStarted
  ) {
    phase = 3;
  } else if (createRsaStarted || createRsaCompleted || analysisStarted) {
    phase = 2;
  }

  const failedStepIndex = groupStats.findIndex((group) => group.failed) + 1;
  const hasFailedStep = failedStepIndex > 0;

  const steps = groupStats.map((group, idx) => {
    const stepIndex = idx + 1;
    let status = "pending";

    if (isEffectivelyCompleted) {
      status = "completed";
    } else if (hasFailedStep && stepIndex === failedStepIndex) {
      status = "failed";
    } else if (runStatus === "failed" && stepIndex === phase) {
      status = "failed";
    } else if (stepIndex < phase) {
      status = "completed";
    } else if (stepIndex === phase) {
      status = "running";
    }

    let stepProgress = 0;
    if (status === "completed") {
      stepProgress = 100;
    } else if (status === "failed") {
      stepProgress = Math.max(group.progress || 0, 90);
    } else if (status === "running") {
      // Drive each phase's % from the AVERAGE of ALL its services
      // (group.progress), not a single proxy service. Previously phase 1
      // keyed off only `file_transfer`, so when that one service hit 100%
      // (it's the LAST preprocessing step, and on a preprocess cache-hit
      // it's the only one that actually runs) the bar jumped to the top
      // of the band while tiling/csf/road_detection/car_removal were still
      // at 0%. The group average reflects real aggregate progress across
      // every stage in the phase.
      if (!hasStartedWork) {
        stepProgress = 0;
      } else {
        const groupAvgProgress = clampProgress(group.progress);
        stepProgress =
          groupAvgProgress > 0 ? groupAvgProgress : group.defaultProgress;
        stepProgress = Math.max(5, Math.min(95, stepProgress));
      }
    }

    let liveMessage = "";
    if (status === "running") {
      if (stepIndex === 1) {
        const candidate = String(fileTransferData?.message || "").trim();
        liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : "";
      } else if (stepIndex === 2) {
        const candidate = String(createRsaData?.message || "").trim();
        liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : "";
        if (!liveMessage) {
          for (const name of analysisServiceNames) {
            const d = getServiceData(services, name);
            const m = String(d?.message || "").trim();
            if (isMeaningfulProgressMessage(m)) {
              liveMessage = m;
              break;
            }
          }
        }
      } else {
        const candidate = String(finalReportData?.message || "").trim();
        liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : "";
        if (!liveMessage) {
          for (const d of deliverableRows) {
            const m = String(d?.message || "").trim();
            if (normalizeServiceStatus(d) === "running" && isMeaningfulProgressMessage(m)) {
              liveMessage = m;
              break;
            }
          }
        }
      }
    }

    // Sub-stages (devs only): one row per service in the group with
    // a known progress entry, so the expanded tile shows the actual
    // pipeline stages (tiling, csf, road_detection, ...) instead of
    // the user-facing rolled-up label. Pending services that haven't
    // emitted yet still appear with status='pending' because
    // initialize_run pre-seeds them (engine=v2). For engine=v1 runs
    // pre-seeding doesn't happen, so only the services that called
    // back show up — that's fine because the user doesn't expand by
    // default.
    const subStages = group.services
      .flatMap((name) => {
        const data = services[name];
        if (!data || typeof data !== "object") return [null];
        // Hide the seeded combined "rsa_analysis" placeholder once any
        // per-worker analysis service (rsa_ssd/rsa_psd/...) is reporting —
        // those ARE the per-type rows (observed live 2026-07-29); the
        // combined row would sit at 0%/Pending forever beside them.
        if (
          name === "rsa_analysis" &&
          ["rsa_ssd", "rsa_psd", "rsa_isd", "rsa_fsa", "rsa_glr"].some(
            (w) => services[w] && typeof services[w] === "object",
          )
        ) {
          return [null];
        }
        // The analysis fan-out runs SEVERAL analysis types' chunks in one
        // stage; the backend attaches per-type {done,total,pct} to its
        // metadata (2026-07-29). Render one row per type — "SSD Analysis",
        // "PSD Analysis", ... — instead of one combined row users read as
        // "SSD only". Falls through to the single combined row for runs
        // planned before the per-type breakdown existed.
        const typeBreakdown =
          name === "rsa_analysis" &&
          data.metadata &&
          typeof data.metadata.analysis_types === "object" &&
          data.metadata.analysis_types !== null
            ? Object.entries(data.metadata.analysis_types)
            : null;
        if (typeBreakdown && typeBreakdown.length > 0) {
          return typeBreakdown.map(([atype, info]) => {
            const done = Number(info?.done) || 0;
            const typeTotal = Number(info?.total) || 0;
            const pct = clampProgress(info?.pct);
            const typeStatus =
              typeTotal > 0 && done >= typeTotal
                ? "completed"
                : done > 0 || pct > 0
                  ? "running"
                  : "pending";
            return {
              name: `rsa_analysis_${String(atype).toLowerCase()}`,
              display_name: `${String(atype).toUpperCase()} Analysis`,
              status: typeStatus,
              progress: typeStatus === "completed" ? 100 : pct,
              message:
                typeStatus === "completed"
                  ? "Completed"
                  : typeStatus === "running"
                    ? "In progress"
                    : "Waiting",
              chunks_done: typeTotal > 0 ? done : null,
              total_chunks: typeTotal > 0 ? typeTotal : null,
              instance_id: data.instance_id || null,
            };
          });
        }
        const subStatus = normalizeServiceStatus(data);
        const subProgress = clampProgress(data.progress);
        const subMessageRaw = String(data.message || "").trim();
        const subMessage = isMeaningfulProgressMessage(subMessageRaw)
          ? subMessageRaw
          : subStatus === "completed"
            ? "Completed"
            : subStatus === "running"
              ? "In progress"
              : subStatus === "failed"
                ? "Failed"
                : "Waiting";
        const meta =
          data.metadata && typeof data.metadata === "object"
            ? data.metadata
            : {};
        const chunksDone =
          typeof meta.chunks_done === "number" ? meta.chunks_done : null;
        const totalChunks =
          typeof meta.total_chunks === "number" ? meta.total_chunks : null;
        return {
          name,
          display_name: SERVICE_DISPLAY_NAMES[name] || name,
          status: subStatus,
          progress: subProgress,
          message: subMessage,
          chunks_done: chunksDone,
          total_chunks: totalChunks,
          instance_id: data.instance_id || null,
        };
      })
      .filter(Boolean);

    return {
      name: group.name,
      display_name: group.display_name,
      mobile_display_name: group.mobile_display_name || null,
      status,
      progress: stepProgress,
      message:
        status === "completed"
          ? "Completed"
          : status === "running"
            ? liveMessage ||
              (hasStartedWork ? "In progress" : "Initializing...")
            : "Waiting",
      started_at: null,
      completed_at: status === "completed" ? new Date() : null,
      duration: null,
      description: group.description,
      instance_id: null,
      folder_id: null,
      folder_name: null,
      sub_stages: subStages,
    };
  });

  const completedSteps = steps.filter((s) => s.status === "completed").length;
  // ── CLIENT FALLBACK ONLY (2026-09-08) ──────────────────────────────────
  // The backend now returns `overall_percent` + `phase` on task rows, the
  // progress GET and queue rows (see InternalComponents/runLabel.js). When
  // present those REPLACE the 3-band number below: getRunListProgressStats and
  // the hero's shownOverallPct read serverOverallPercent() first. The band
  // model is kept for older backends / older runs that carry no server value.
  // Smart progress model with 7+ internal milestones so the bar feels gradual:
  // File Transfer (6-44) -> RSA Analysis (45-79) -> Final Reports (80-99) -> 100.
  // Each phase occupies a band; within the band the % is driven by that
  // phase's GROUP AVERAGE (avg of every service present in the phase), so the
  // overall bar reflects real aggregate progress instead of one proxy service.
  //   File Transfer 6→44 · RSA Analysis 45→79 · Final Reports 80→99 · done 100.
  const fileTransferGroupProgress = clampProgress(groupStats[0]?.progress);
  const rsaAnalysisGroupProgress = clampProgress(groupStats[1]?.progress);
  const finalReportsGroupProgress = clampProgress(groupStats[2]?.progress);

  let overallProgress = 0;
  if (!hasStartedWork && !isEffectivelyCompleted && runStatus !== "failed") {
    overallProgress = 0;
  } else if (isEffectivelyCompleted) {
    overallProgress = 100;
  } else if (phase === 1) {
    overallProgress = Math.round(6 + (fileTransferGroupProgress / 100) * 38);
    if (groupStats[0]?.running || groupStats[0]?.completed) {
      overallProgress = Math.max(overallProgress, 12);
    }
  } else if (phase === 2) {
    overallProgress = Math.round(45 + (rsaAnalysisGroupProgress / 100) * 34);
    if (createRsaStarted || analysisStarted) {
      overallProgress = Math.max(overallProgress, 50);
    }
  } else {
    overallProgress = Math.round(80 + (finalReportsGroupProgress / 100) * 19);
    if (pdfReportStarted || shapefileStarted) {
      overallProgress = Math.max(overallProgress, 86);
    }
  }
  overallProgress = clampProgress(overallProgress);

  const overallStatus =
    runStatus === "failed"
      ? "failed"
      : isEffectivelyCompleted
        ? "completed"
        : steps.some((s) => s.status === "running")
          ? "running"
          : "pending";

  return {
    status: overallStatus,
    progress: {
      percentage: overallStatus === "completed" ? 100 : overallProgress,
      completed_steps:
        overallStatus === "completed" ? steps.length : completedSteps,
      total_steps: steps.length,
    },
    steps,
  };
};

/**
 * Progress % and step count for dashboard run list tiles — matches main dashboard
 * (RSA model uses three overview groups; pipeline runs use Redis/API progress when present).
 */
export const getRunListProgressStats = (run, progress) => {
  if (!run) return { progressPct: 0, stepsCount: 0 };

  const runProjectType = String(
    run.project_type || run?.metadata?.project_type || "",
  )
    .trim()
    .toLowerCase();

  // Server percent (contract `overall_percent`, 2026-09-08) replaces the
  // client 3-band derivation whenever the backend sends it.
  const serverPct = serverOverallPercent(run, progress);

  if (RSA_MODEL_PROJECT_TYPES.has(runProjectType)) {
    const overview = buildRsaModelOverview(run, progress);
    return {
      progressPct: monotonicPercent(
        run.run_id,
        serverPct ?? Math.round(Number(overview.progress.percentage)),
        overview.status,
      ),
      stepsCount: overview.steps.length,
    };
  }

  if (serverPct != null) {
    const totalSvc =
      progress && typeof progress === "object" ? Number(progress.total_services) : NaN;
    return {
      progressPct: monotonicPercent(
        run.run_id,
        serverPct,
        (progress && typeof progress === "object" && progress.status) || run.status,
      ),
      stepsCount:
        Number.isFinite(totalSvc) && totalSvc > 0
          ? totalSvc
          : Array.isArray(run.services)
            ? run.services.length
            : 0,
    };
  }

  const hasProgressObj = progress && typeof progress === "object";
  const rawOverall =
    hasProgressObj && "overall_progress" in progress
      ? Number(progress.overall_progress)
      : Number(run.overall_progress ?? 0);
  const progressPct = monotonicPercent(
    run.run_id,
    Number.isFinite(rawOverall) ? Math.round(rawOverall) : 0,
    (hasProgressObj && progress.status) || run.status,
  );

  const totalSvc = hasProgressObj ? Number(progress.total_services) : NaN;
  const stepsFromProgress =
    Number.isFinite(totalSvc) && totalSvc > 0 ? totalSvc : 0;
  const stepsFromRun = Array.isArray(run.services) ? run.services.length : 0;
  const stepsFromKeys =
    hasProgressObj && progress.services && typeof progress.services === "object"
      ? Object.keys(progress.services).length
      : 0;
  const stepsCount =
    stepsFromProgress > 0
      ? stepsFromProgress
      : stepsFromRun > 0
        ? stepsFromRun
        : stepsFromKeys;

  return { progressPct, stepsCount };
};

// Step-group name (RSA_MODEL_STEP_GROUPS) → contract phase key.
const STEP_NAME_TO_PHASE = {
  file_transfer: "preprocess",
  rsa_analysis: "analysis",
  final_reports: "deliverables",
};

/**
 * Phase chip data for a run tile / row: `{ phase, label }` with phase one of
 * queued|preprocess|analysis|deliverables|done|failed. The backend's `phase` +
 * `phase_label` (task row, progress GET or queue entry) win; otherwise it is
 * derived from the same steps model the dashboard draws (CLIENT FALLBACK).
 */
export const getRunListPhase = (run, progress, entry = null) => {
  if (!run) return null;
  const fromServer = serverPhase(run, progress, entry);
  if (fromServer) return fromServer;

  const queueStatus = queuePreExecutionStatus(run, progress);
  if (queueStatus === "queued") return { phase: "queued", label: PHASES.queued.label };
  if (queueStatus === "cancelled") return { phase: "queued", label: "Cancelled" };
  const runStatus = String(run?.status || "").trim().toLowerCase();
  if (runStatus === "queued") return { phase: "queued", label: PHASES.queued.label };
  if (runStatus === "failed") return { phase: "failed", label: PHASES.failed.label };
  if (runStatus === "completed") return { phase: "done", label: PHASES.done.label };

  const overview = buildRsaModelOverview(run, progress);
  const steps = overview.steps || [];
  const failed = steps.find((s) => s.status === "failed");
  if (failed) return { phase: "failed", label: PHASES.failed.label };
  const running = steps.find((s) => s.status === "running");
  if (running) {
    const phase = normalizePhase(STEP_NAME_TO_PHASE[running.name] || running.name);
    if (phase) return { phase, label: PHASES[phase].label };
  }
  if (overview.status === "completed") return { phase: "done", label: PHASES.done.label };
  if (steps.length && steps.every((s) => s.status === "pending")) {
    return { phase: "queued", label: "Starting" };
  }
  return null;
};

/**
 * Label for the run list tile: current / active pipeline phase from the same steps model as the dashboard.
 * Works for RSA model (grouped steps from {@link buildRsaModelOverview}) and generic runs ({@link transformProgressData}).
 */
export const getDashboardRunActiveStepDisplay = (run, progress) => {
  if (!run) return "";

  const runProjectType = String(
    run.project_type || run?.metadata?.project_type || "",
  )
    .trim()
    .toLowerCase();

  let steps = [];

  if (RSA_MODEL_PROJECT_TYPES.has(runProjectType)) {
    const overview = buildRsaModelOverview(run, progress);
    steps = overview.steps || [];
  } else {
    const hasProgressObj = progress && typeof progress === "object";
    const services =
      hasProgressObj &&
      progress.services &&
      typeof progress.services === "object"
        ? progress.services
        : null;
    if (services && Object.keys(services).length > 0) {
      const transformed = transformProgressData(progress, {
        run_id: run.run_id,
        project_id: run.project_id,
        project_name: run.project_name,
        created_at: run.created_at,
        user_email: run.user_email,
      });
      steps = transformed.steps || [];
    }
  }

  if (!steps.length) return "";

  // Queued / cancelled runs have no active step: label the tile with the
  // queue state instead of the first pending phase ("Pre-Process").
  const queueStatus = queuePreExecutionStatus(run, progress);
  if (queueStatus) return queueStateMessage(queueStatus, run, progress) || "";

  const isStepActive = (s) => {
    const st = String(s?.status || "")
      .trim()
      .toLowerCase();
    return st === "running" || st === "processing";
  };
  const running = steps.find(isStepActive);
  if (running) return running.display_name || running.name || "";

  const failed = steps.find((s) => s.status === "failed");
  if (failed) return failed.display_name || failed.name || "";

  const runStatus = String(run?.status || "")
    .trim()
    .toLowerCase();
  if (
    runStatus === "completed" ||
    steps.every((s) => s.status === "completed")
  ) {
    const last = steps[steps.length - 1];
    return last ? last.display_name || last.name || "" : "";
  }

  const pending = steps.find((s) => s.status === "pending");
  if (pending) return pending.display_name || pending.name || "";

  return steps[0]?.display_name || steps[0]?.name || "";
};

const AnalysisDashboard = ({
  selectedRun,
  setSelectedRun,
  mockProjectsData,
  displayRuns,
  runProgressMap = {},
  loading = false,
  error = null,
  onBrowseDeliverables = null,
  setActiveMainTab = null,
  // Accepted for caller compatibility (the mobile run sheet passes it); the
  // per-step all-phases rendering it used to enable was retired 2026-07-29 —
  // the sheet now follows the active phase like the desktop inspector column.
  showAllPhases = false, // eslint-disable-line no-unused-vars
  // Deliverables regeneration (2026-09-07): opens the "Regenerate deliverables…"
  // modal for the selected run; offered only for completed runs whose id is in
  // regenerableRunIds (= the Pipeline backend holds a manifest for them).
  onRegenerateRun = null,
  regenerableRunIds = {},
}) => {
  // Use props for selectedRun and setSelectedRun, or create local state if not provided
  const [localSelectedRun, setLocalSelectedRun] = useState(null);

  // Which phase's sub-stages are shown in the always-visible right-hand
  // inspector pane. Single string (or null). null means "no explicit
  // selection" — the inspector then falls back to the running/first step.
  // Clicking a step title selects that step; only one phase is inspected at
  // a time because the inspector occupies a single fixed grid slot. The
  // reserved slot keeps the timeline / running-task / overall progress tiles
  // from moving when the user switches between phases.
  const [activePhase, setActivePhase] = useState(null);
  const currentSelectedRun =
    selectedRun !== undefined ? selectedRun : localSelectedRun;
  const currentSetSelectedRun = setSelectedRun || setLocalSelectedRun;

  // Preprocess-cache LED: does this project have a completed orthomap-keyed
  // preprocess cache (Tiling/CSF/RD/CR products), i.e. will the next run on
  // the same inputs skip cold preprocessing? Backed by the Pipeline backend's
  // GET /rsa/projects/<id>/preprocess-cache (one indexed MySQL read; the row
  // only exists after cache_writer's marker-last insert, so lit == genuinely
  // usable). Refetched on project change and every 60s — the cache write is
  // async since 2026-07-29, so the LED can light up mid-run.
  const [preprocessCache, setPreprocessCache] = useState(null);
  const cacheProjectId = useMemo(() => {
    const raw = String(
      currentSelectedRun?.project_id ??
        currentSelectedRun?.metadata?.source_project_id ??
        "",
    );
    const numeric = raw.replace(/^project_/, "");
    return /^\d+$/.test(numeric) ? numeric : null;
  }, [currentSelectedRun]);
  useEffect(() => {
    if (!cacheProjectId) {
      setPreprocessCache(null);
      return undefined;
    }
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const resp = await fetch(
          `${processApi}/rsa/projects/${cacheProjectId}/preprocess-cache`,
        );
        const body = await resp.json();
        if (!cancelled) setPreprocessCache(body);
      } catch {
        if (!cancelled) setPreprocessCache(null);
      }
    };
    fetchStatus();
    const timer = setInterval(fetchStatus, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cacheProjectId]);

  // Dot-only LED, rendered immediately LEFT of the run-status chip (per
  // product direction 2026-07-29: no text label; hover explains). Rendered
  // twice: desktop hero tile + (with the --mobile modifier) the Overall
  // Progress header, because the hero tile is display:none on phones.
  const renderPreprocessLed = (extraClass = "") => {
    const lit = Boolean(preprocessCache?.available);
    const tooltip = lit
      ? "Pre-process cache enabled"
      : "No pre-process cache yet — the next run preprocesses from scratch";
    return (
      <span
        className={`preprocess-cache-led${lit ? " lit" : ""}${extraClass ? ` ${extraClass}` : ""}`}
        title={tooltip}
        aria-label={tooltip}
      >
        <span className="preprocess-cache-led-dot" />
      </span>
    );
  };

  // Get current analysis data (transformed progress data)
  // Use specific run's progress instead of entire runProgress object to prevent infinite loops
  const currentRunProgress = currentSelectedRun
    ? runProgressMap[currentSelectedRun.run_id]
    : null;
  const currentAnalysisData = useMemo(() => {
    if (!currentSelectedRun) return null;

    const initiatorEmail =
      currentSelectedRun.user_email ||
      currentSelectedRun?.metadata?.user_email ||
      null;

    const fallbackRunProgress =
      currentSelectedRun?.services_progress &&
      typeof currentSelectedRun.services_progress === "object" &&
      Object.keys(currentSelectedRun.services_progress).length > 0
        ? {
            overall_progress: currentSelectedRun.overall_progress ?? 0,
            services: currentSelectedRun.services_progress,
            completed_services: Object.values(
              currentSelectedRun.services_progress,
            ).filter((svc) => {
              const status = String(svc?.status || "")
                .trim()
                .toLowerCase();
              return (
                status === "completed" || Number(svc?.progress || 0) >= 100
              );
            }).length,
            total_services: Object.keys(currentSelectedRun.services_progress)
              .length,
          }
        : null;
    const progress = currentRunProgress || fallbackRunProgress;
    const runProjectType = String(
      currentSelectedRun.project_type ||
        currentSelectedRun?.metadata?.project_type ||
        "",
    )
      .trim()
      .toLowerCase();
    const isRsaModelRun = RSA_MODEL_PROJECT_TYPES.has(runProjectType);

    // Run-queue pre-execution states apply to every project type: 0%, all
    // phases pending, "waiting for capacity" — never the Pre-process band.
    if (isRsaModelRun || queuePreExecutionStatus(currentSelectedRun, progress)) {
      const overview = buildRsaModelOverview(currentSelectedRun, progress);
      return {
        id: currentSelectedRun.run_id,
        project_id: currentSelectedRun.project_id,
        project_name:
          currentSelectedRun.project_name || currentSelectedRun.name,
        status: overview.status,
        progress: overview.progress,
        steps: overview.steps,
        queue_message: overview.queue_message || null,
        start_time:
          currentSelectedRun.created_at ||
          currentSelectedRun.last_updated ||
          new Date(),
        initiator_email: initiatorEmail,
      };
    }

    // Check if progress has meaningful data (has services with data)
    const hasProgressData =
      progress &&
      progress.services &&
      typeof progress.services === "object" &&
      Object.keys(progress.services).length > 0;

    if (!hasProgressData) {
      // Only show initial steps if services are available
      // If Redis was cleared and this is a new run (< 20 hours), services will be empty
      // In that case, don't show anything (as per user requirement)
      if (
        !currentSelectedRun.services ||
        currentSelectedRun.services.length === 0
      ) {
        // Check if this is from MongoDB (old task > 20 hours) - if so, we should still show it
        const isOldTask =
          currentSelectedRun.source === "mongodb" ||
          currentSelectedRun.data_source === "mongodb";
        if (!isOldTask) {
          // New task without services (Redis was cleared) - don't show anything
          console.warn(
            "⚠️ No services found for run (Redis likely cleared):",
            currentSelectedRun.run_id,
          );
          return {
            id: currentSelectedRun.run_id,
            project_id: currentSelectedRun.project_id,
            project_name: currentSelectedRun.name,
            status: currentSelectedRun.status || "pending",
            progress: {
              percentage: 0,
              completed_steps: 0,
              total_steps: 0,
            },
            steps: [],
            start_time:
              currentSelectedRun.created_at ||
              currentSelectedRun.last_updated ||
              new Date(),
            initiator_email: initiatorEmail,
          };
        }
      }

      // Create initial steps from currentSelectedRun.services if available
      // This ensures progress circles appear immediately after launch
      const getServiceDescription = (name) => {
        const descriptions = {
          tiling: "Creating tiles for processing",
          csf: "Classifying ground points",
          car_removal: "Removing vehicles from scene",
          road_detection: "Detecting road boundaries",
          embankment: "Analyzing road embankments",
          horizontal: "Calculating horizontal curves and alignments",
          slope_calc: "Calculating slope values and gradients",
          psd_classification:
            "Classifying PSD (Particle Size Distribution) data",
          automatic_report: "Generating automatic analysis reports",
          report_generation: "Generating final analysis report",
        };
        return descriptions[name] || `Processing ${name}`;
      };

      // Sort services by execution order
      const sortedServices = [...(currentSelectedRun.services || [])].sort(
        (a, b) => {
          const indexA = SERVICE_ORDER.indexOf(a);
          const indexB = SERVICE_ORDER.indexOf(b);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        },
      );

      const initialSteps = sortedServices.map((serviceName, index) => {
        const isFirst = index === 0;

        return {
          name: serviceName,
          display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
          status: isFirst ? "running" : "pending", // First step is running to show orange glow
          progress: isFirst ? 0 : 0, // First step starts at 0% but is "running"
          message: isFirst ? "Initializing..." : "Waiting...",
          started_at: null,
          completed_at: null,
          duration: null,
          description: getServiceDescription(serviceName),
          instance_id: null,
          folder_id: null,
          folder_name: null,
        };
      });

      // If no services found, log warning but still return structure
      if (initialSteps.length === 0) {
        console.warn(
          "⚠️ No services found for run:",
          currentSelectedRun.run_id,
          "Run data:",
          currentSelectedRun,
        );
      }

      // Return basic structure with initial steps (so analyses-list / steps-timeline / overall-progress show)
      return {
        id: currentSelectedRun.run_id,
        project_id: currentSelectedRun.project_id,
        project_name:
          currentSelectedRun.project_name || currentSelectedRun.name,
        status: currentSelectedRun.status || "pending",
        progress: {
          percentage: currentSelectedRun.overall_progress ?? 0,
          completed_steps: 0,
          total_steps: initialSteps.length || 0,
        },
        steps: initialSteps,
        start_time:
          currentSelectedRun.created_at ||
          currentSelectedRun.last_updated ||
          new Date(),
        initiator_email: initiatorEmail,
      };
    }

    const transformed = transformProgressData(progress, {
      run_id: currentSelectedRun.run_id,
      project_id: currentSelectedRun.project_id,
      project_name: currentSelectedRun.project_name || currentSelectedRun.name, // Use project_name if available
      created_at: currentSelectedRun.created_at,
      user_email: currentSelectedRun.user_email,
      input_files: [],
    });

    // If transformed data has no steps but we have services, merge with initial steps
    if (
      (!transformed.steps || transformed.steps.length === 0) &&
      currentSelectedRun.services &&
      currentSelectedRun.services.length > 0
    ) {
      const getServiceDescription = (name) => {
        const descriptions = {
          tiling: "Creating tiles for processing",
          csf: "Classifying ground points",
          car_removal: "Removing vehicles from scene",
          road_detection: "Detecting road boundaries",
          embankment: "Analyzing road embankments",
          horizontal: "Calculating horizontal curves and alignments",
          slope_calc: "Calculating slope values and gradients",
          psd_classification:
            "Classifying PSD (Particle Size Distribution) data",
          automatic_report: "Generating automatic analysis reports",
          report_generation: "Generating final analysis report",
        };
        return descriptions[name] || `Processing ${name}`;
      };

      const sortedServices = [...(currentSelectedRun.services || [])].sort(
        (a, b) => {
          const indexA = SERVICE_ORDER.indexOf(a);
          const indexB = SERVICE_ORDER.indexOf(b);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        },
      );

      transformed.steps = sortedServices.map((serviceName, index) => {
        const isFirst = index === 0;
        return {
          name: serviceName,
          display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
          status: isFirst ? "running" : "pending", // First step is running to show orange glow
          progress: 0,
          message: isFirst ? "Initializing..." : "Waiting...",
          started_at: null,
          completed_at: null,
          duration: null,
          description: getServiceDescription(serviceName),
          instance_id: null,
          folder_id: null,
          folder_name: null,
        };
      });

      transformed.progress = {
        ...transformed.progress,
        total_steps: transformed.steps.length,
      };
    }

    return { ...transformed, initiator_email: initiatorEmail };
  }, [currentSelectedRun, currentRunProgress]);

  // Auto-select first run on load
  // TEMPORARILY DISABLED: Commented out to prevent server overload while component is WIP
  // useEffect(() => {
  //     if (runs.length > 0 && !selectedRun) {
  //         setSelectedRun(runs[0]);
  //     }
  // }, [runs, selectedRun]);



  // No need for useEffect - data loaded via hooks

  const getStatusClass = (status) => {
    switch (status) {
      case "completed":
        return "status-completed";
      case "processing":
      case "running":
        return "status-running";
      case "failed":
        return "status-failed";
      case "queued":
        return "status-queued";
      case "cancelled":
        return "status-cancelled";
      case "pending":
        return "status-pending";
      default:
        return "status-pending";
    }
  };


  const runsToUse = displayRuns || [];
  const displayLoading = Boolean(loading);
  const displayError = error;

  // Auto-select first run if none selected
  useEffect(() => {
    if (runsToUse.length > 0 && !currentSelectedRun) {
      currentSetSelectedRun(runsToUse[0]);
    }
  }, [runsToUse.length, currentSetSelectedRun, currentSelectedRun]);

  // currentAnalysisData (from API/Redis run + services) or nothing —
  // no demo fallback.
  const displayAnalysisData = currentAnalysisData || null;
  // Monotonic display value for the Overall Progress tile (see monotonicPercent).
  // The backend's `overall_percent` (2026-09-08 contract) replaces the client
  // 3-band number whenever it is present on the progress GET or the task row.
  const serverPct = serverOverallPercent(currentSelectedRun, currentRunProgress);
  const shownOverallPct = displayAnalysisData
    ? monotonicPercent(
        currentSelectedRun?.run_id,
        serverPct ?? displayAnalysisData.progress?.percentage,
        displayAnalysisData.status,
      )
    : 0;

  const rsaModelPayloadAnalysisLabels = useMemo(() => {
    const pt = String(
      currentSelectedRun?.project_type ||
        currentSelectedRun?.metadata?.project_type ||
        "",
    )
      .trim()
      .toLowerCase();
    if (pt !== "rsa-model") return [];
    return getRsaModelPayloadAnalysisLabels(currentSelectedRun);
  }, [currentSelectedRun]);

  // Phase auto-follow: when the RUNNING phase changes (Pre-Process ->
  // RSA Analysis -> Reports & Deliverables) or the selected run changes,
  // drop any manual phase selection so the stage detail moves with the run.
  // Without this the detail stuck on Pre-Process after phase 1 completed
  // (reported on mobile 2026-07-29 — same logic serves desktop).
  const phaseFollowKey = useMemo(() => {
    const steps = displayAnalysisData?.steps || [];
    const running = steps.find(
      (s) => s.status === "running" || s.status === "failed",
    );
    return `${currentSelectedRun?.run_id || ""}:${running?.name || "done"}`;
  }, [displayAnalysisData, currentSelectedRun]);
  useEffect(() => {
    setActivePhase(null);
  }, [phaseFollowKey]);

  // Format date helper
  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Return only the main content area (without wrapper and projects panel)
  return (
    <div className="main-content-area-dashboard">
      {displayLoading ? (
        <div className="analysis-dashboard-loading-spinner">
          <div className="spinner"></div>
          <p>Loading Analysis Dashboard...</p>
        </div>
      ) : displayError ? (
        <div className="dashboard-error">
          <p>Error: {displayError}</p>
        </div>
      ) : !displayAnalysisData ? (
        <div className="analyses-container">
          <div className="no-analyses">
            <p>
              No analysis selected. Please select a project from the right
              panel.
            </p>
          </div>
        </div>
      ) : (
        (() => {
          // The phase inspector is always visible. It shows the step the
          // user selected (by clicking its title), falling back to the
          // RUNNING step, then the first step that has pipeline stages.
          // A user selection is only honored while the run stays in the
          // same phase: `phaseFollowKey` (effect below) clears it when the
          // running phase advances, so the stage detail follows the run
          // into RSA Analysis and Reports & Deliverables instead of
          // sticking on Pre-Process (mobile bug, 2026-07-29).
          const allSteps = displayAnalysisData.steps || [];
          const stepsWithSubStages = allSteps.filter(
            (s) => Array.isArray(s.sub_stages) && s.sub_stages.length > 0,
          );
          // Prefer a step that actually carries pipeline-stage detail, but NEVER
          // let the absence of detail collapse the inspector — fall through to
          // the plain step list. sub_stages are built purely from keys present in
          // progress.services, and that object is legitimately empty in several
          // ordinary cases: no SSE stream for a terminal run, engine=v1 runs, and
          // (until the initialize_run collection-name fix) any run older than 20h
          // served from MongoDB. Gating activeStepObj on sub_stages made the whole
          // panel vanish in exactly the same conditions that pin the bar at 0% —
          // the "0% ⇒ no phases page" report. The single-row fallback further down
          // in phase-inspector-body was written for this case but was unreachable.
          // An explicit user selection wins over both lists so tapping any step
          // title in the timeline always moves the inspector.
          const activeStepObj =
            (activePhase && allSteps.find((s) => s.name === activePhase)) ||
            stepsWithSubStages.find(
              (s) => s.status === "running" || s.status === "failed",
            ) ||
            stepsWithSubStages[stepsWithSubStages.length - 1] ||
            allSteps.find(
              (s) => s.status === "running" || s.status === "failed",
            ) ||
            allSteps[allSteps.length - 1] ||
            null;
          return (
            <div className="analyses-container">
              <div
                className={`analyses-list analyses-list--grid${activeStepObj ? " analyses-list--grid-open" : ""}`}
              >
                {/* Project Header — spans both columns */}
                <div className="dashboard-tile-bg dashboard-hero-tile">
                  <div className="analysis-dashboard-project-header">
                    {/* Run label (2026-09-08): "SR143s1-1 · MP 0–12.3" with the
                        route/direction/miles line under it; older runs without
                        a label read "<project> · <analysis types>". */}
                    <div className="dashboard-hero-title" data-testid="dashboard-hero-title">
                      <RunTitle
                        run={currentSelectedRun}
                        progress={currentRunProgress}
                        as="h4"
                      />
                      {currentSelectedRun?.run_label?.display && (
                        <span
                          className="run-subtitle dashboard-hero-project"
                          title={displayAnalysisData.project_name}
                        >
                          {displayAnalysisData.project_name}
                        </span>
                      )}
                      {/* Member of a Project Overview → a chip that opens it. */}
                      <OverviewLinks projectId={currentSelectedRun?.project_id} />
                    </div>
                    {/* Preprocess-cache LED: lit green when a completed
                        orthomap-keyed preprocess cache exists for this
                        project (next run skips Tiling/CSF/RD/CR). */}
                    {renderPreprocessLed()}
                    {currentSelectedRun?.metadata?.regeneration?.source_run_id && (
                      <span
                        className="dashboard-regeneration-note"
                        title={`Deliverables regenerated from run ${currentSelectedRun.metadata.regeneration.source_run_id}`}
                      >
                        <i className="fa-solid fa-rotate" aria-hidden /> Regeneration of{" "}
                        {shortenRunDisplayId(currentSelectedRun.metadata.regeneration.source_run_id)}
                      </span>
                    )}
                    {onRegenerateRun &&
                      isRunRegenerable(currentSelectedRun, regenerableRunIds) && (
                        <button
                          type="button"
                          className="dashboard-regenerate-btn"
                          title="Regenerate this run's deliverables (online report, videos, shapefile + CSV + DXF, automatic reports) from its stored data — no analysis re-run, no billing"
                          onClick={() => onRegenerateRun(currentSelectedRun)}
                        >
                          <i className="fa-solid fa-rotate" aria-hidden /> Regenerate deliverables…
                        </button>
                      )}
                    <span
                      className={`project-status ${getStatusClass(displayAnalysisData.status)}`}
                    >
                      {displayAnalysisData.status}
                    </span>
                  </div>
                </div>

                {/* Steps Timeline — left column. Clicking a step's title
                            selects that phase and populates the right-column
                            inspector without displacing any other tile. */}
                <div className="steps-timeline-container dashboard-tile-bg">
                  <div className="steps-timeline">
                    {displayAnalysisData.steps.map((step, index) => {
                      const subStages = Array.isArray(step.sub_stages)
                        ? step.sub_stages
                        : [];
                      const hasSubStages = subStages.length > 0;
                      // Highlight the step whose details are actually
                      // shown — including the default (running/first)
                      // step when nothing has been explicitly selected.
                      const isActive =
                        !!activeStepObj && activeStepObj.name === step.name;
                      return (
                        <div
                          key={step.name}
                          className={`step-item ${getStatusClass(step.status)}`}
                          data-active={isActive ? "true" : "false"}
                        >
                          <div className="step-status">
                            <span className="status-text">{step.status}</span>
                          </div>
                          <div className="step-circle-wrapper">
                            <div className="step-icon">
                              <span className="step-number">{index + 1}</span>
                            </div>
                          </div>
                          <div className="step-info">
                            {/* The step title doubles as a button that loads this
                                                    step's pipeline stages into the always-visible inspector. */}
                            {/* Selectable even without sub-stage detail: the
                                inspector now falls back to a single row carrying
                                the step's own status, so every phase has
                                something to show. */}
                            <button
                              type="button"
                              className="step-info-title-btn"
                              onClick={() => setActivePhase(step.name)}
                              aria-pressed={isActive}
                              title={
                                hasSubStages
                                  ? "Show this step's pipeline stages in the inspector"
                                  : "No pipeline stages reported yet — shows this step's own status"
                              }
                            >
                              {step.mobile_display_name ? (
                                <>
                                  {/* Two labels, one visible at a time (CSS
                                      swaps them at ≤768px) — display:none keeps
                                      the hidden one out of the a11y tree, so
                                      the button's name always matches what is
                                      on screen. */}
                                  <span className="step-info-title-wide">
                                    {step.display_name}
                                  </span>
                                  <span className="step-info-title-narrow">
                                    {step.mobile_display_name}
                                  </span>
                                </>
                              ) : (
                                step.display_name
                              )}
                            </button>
                            {/* Per-step CloudWatch logs link removed — each pipeline
                                                    sub-stage in the inspector has its own logs link. */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Phase Inspector — right column, always visible. It shows
                            the step selected by clicking a step title (or the
                            running/first step by default). Selecting another step
                            swaps the content; the slot itself never collapses. */}
                {/* ONE inspector everywhere (2026-07-29): the mobile sheet used
                    to render an inspector per step (showAllPhases), which
                    buried the current phase's stages under the completed
                    Pre-Process block and pushed Overall Progress below the
                    fold — read as "stuck on Pre-Process, no total progress".
                    The sheet now follows the ACTIVE phase exactly like the
                    desktop column (phase auto-follow advances it; the step
                    titles in the timeline remain tap-to-inspect). */}
                {(activeStepObj ? [activeStepObj] : []).map((phaseObj) => (
                  <aside
                    key={phaseObj.name}
                    className={`phase-inspector dashboard-tile-bg phase-inspector--active ${getStatusClass(phaseObj.status)}`}
                    aria-live="polite"
                  >
                    <div className="phase-inspector-header">
                      <i className="fas fa-microchip"></i>
                      <span className="phase-inspector-title">
                        {phaseObj.display_name}
                      </span>
                      <span className="phase-inspector-count">
                        {phaseObj.sub_stages && phaseObj.sub_stages.length
                          ? `${phaseObj.sub_stages.length} pipeline stages`
                          : "no stage detail yet"}
                      </span>
                    </div>
                    <div className="phase-inspector-body">
                      {/* Sub-stages only exist when per-service progress is
                          available (progress.services). That is frequently
                          absent — Redis cleared, engine=v1 runs, or a run that
                          hasn't emitted yet — which is why this panel used to
                          disappear entirely. Fall back to one row carrying the
                          step's own status/progress so it always says something. */}
                      {(phaseObj.sub_stages && phaseObj.sub_stages.length
                        ? phaseObj.sub_stages
                        : [
                            {
                              name: phaseObj.name,
                              display_name: phaseObj.display_name,
                              status: phaseObj.status,
                              progress: phaseObj.progress ?? 0,
                              message:
                                phaseObj.message ||
                                "No pipeline stage detail reported yet",
                              chunks_done: null,
                              total_chunks: null,
                            },
                          ]
                      ).map((sub) => {
                        const chunkSuffix =
                          sub.total_chunks && sub.chunks_done != null
                            ? ` (${sub.chunks_done}/${sub.total_chunks})`
                            : "";
                        return (
                          <div
                            key={sub.name}
                            className={`sub-stage-row ${getStatusClass(sub.status)}`}
                          >
                            <div className="sub-stage-name">
                              <span className="sub-stage-label">
                                {sub.display_name}
                              </span>
                              <span className="sub-stage-status">
                                {sub.status}
                                {chunkSuffix}
                              </span>
                            </div>
                            <div className="sub-stage-bar-wrap">
                              <span className="sub-stage-progress-text">
                                {sub.progress}%
                              </span>
                              <div className="sub-stage-bar-container">
                                <div
                                  className={`sub-stage-bar-fill ${getStatusClass(sub.status)}`}
                                  style={{ width: `${sub.progress}%` }}
                                />
                              </div>
                            </div>
                            <div className="sub-stage-meta">
                              <span
                                className="sub-stage-message"
                                title={sub.message}
                              >
                                {sub.message}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </aside>
                ))}

                {/* Running Task — left column, always visible. */}
                <div className="analysis-dashboard-task-info analysis-dashboard-task-info--running dashboard-tile-bg">
                  <div className="task-info-details-header-container">
                    <h5 className="task-info-details-header">
                      Running Task(s):
                    </h5>
                    <div className="task-info-details-tags">
                      {rsaModelPayloadAnalysisLabels.length > 0 ? (
                        rsaModelPayloadAnalysisLabels.map((label, idx) => (
                          <span key={`${label}-${idx}`} className="summary-tag">
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="summary-tag">
                          {displayAnalysisData.steps?.find(
                            (s) => s.status === "running",
                          )?.display_name ||
                            displayAnalysisData.steps?.[0]?.display_name ||
                            "Analysis"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">
                      <i className="fas fa-hashtag"></i>
                      Task ID:
                    </span>
                    <span
                      className="task-info-value"
                      title={displayAnalysisData.id}
                    >
                      <RunIdTail
                        runId={displayAnalysisData.id}
                        text={shortenRunDisplayId(displayAnalysisData.id)}
                      />
                      {buildSfConsoleUrl(displayAnalysisData.id) && (
                        <a
                          href={buildSfConsoleUrl(displayAnalysisData.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="task-info-aws-link"
                          title="Open this run in the AWS Step Functions console"
                          style={{ marginLeft: 8 }}
                        >
                          <i className="fas fa-external-link-alt"></i> SF
                          console
                        </a>
                      )}
                    </span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">
                      <i className="fas fa-user"></i>
                      Initiator:
                    </span>
                    <span className="task-info-value">
                      {displayAnalysisData.initiator_email || "—"}
                    </span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">
                      <i className="fas fa-clock"></i>
                      Start Time:
                    </span>
                    <span className="task-info-value">
                      {formatDateTime(displayAnalysisData.start_time)}
                    </span>
                  </div>
                </div>

                {/* Overall Progress — spans both columns */}
                <div className="overall-progress dashboard-tile-bg dashboard-overall-progress-tile">
                  <div className="progress-header">
                    <span>Overall Progress</span>
                    {/* Mobile-only LED instance — the desktop hero tile
                        (which carries the primary LED) is hidden on phones. */}
                    {renderPreprocessLed("preprocess-cache-led--mobile")}

                    {(() => {
                      const activeStep = displayAnalysisData.steps?.find(
                        (s) => {
                          const stepStatus = String(s?.status || "")
                            .trim()
                            .toLowerCase();
                          return (
                            stepStatus === "running" ||
                            stepStatus === "processing"
                          );
                        },
                      );

                      const genericMsgs = new Set([
                        "Initializing...",
                        "In progress",
                        "Waiting",
                        "Completed",
                        "",
                      ]);

                      const liveMsg =
                        activeStep?.message &&
                        !genericMsgs.has(activeStep.message)
                          ? activeStep.message
                          : null;

                      const queueMsg =
                        displayAnalysisData.queue_message || null;
                      const fallback = activeStep
                        ? activeStep.description || activeStep.display_name
                        : `${displayAnalysisData.progress.completed_steps} of ${displayAnalysisData.progress.total_steps} steps completed`;

                      const text = queueMsg || liveMsg || fallback;

                      return (
                        <span className="progress-text" key={text}>
                          {text}
                        </span>
                      );
                    })()}

                    <span className="progress-percentage">
                      {shownOverallPct}%
                    </span>
                  </div>

                  <div className="progress-bar-container">
                    <div
                      className={`progress-bar-fill ${getStatusClass(displayAnalysisData.status)}`}
                      style={{
                        width: `${shownOverallPct}%`,
                      }}
                    ></div>
                    {/* Mobile-only twin of the header percentage — sits at the
                        end of the bar row (≤768px); display:none on desktop. */}
                    <span
                      className="progress-percentage progress-percentage--in-bar"
                      aria-hidden="true"
                    >
                      {shownOverallPct}%
                    </span>
                  </div>
                  {setActiveMainTab && (
                    <div className="dashboard-nav-footer">
                      <button
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                        onClick={() => setActiveMainTab("routes")}
                      >
                        <i className="fas fa-arrow-left"></i> Back: Tasks &amp;
                        Routes
                      </button>
                      <button
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                        onClick={() => setActiveMainTab("deliverables")}
                      >
                        Next: Deliverables{" "}
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
};

export default AnalysisDashboard;
